import type { SearchSuggestion } from '@/api/v4/search.api';
import { useEnrichedProjectSuggestions } from './use-enriched-project-suggestions.query';
import type { EnrichedProjectSearchSuggestion } from '@/api/v4/project-enrich.api';
import { useMemo } from 'react';
import { combineProjectData } from './combine-project-data';
import { useProjectSuggestionsTableConfig } from './use-project-suggestions-table-config';
import { TableHeaderCell } from '@/ui/table/infinite-table/table-header-cell';
import { TableBody } from '@/ui/table/infinite-table/table-body';
import { colors } from '@/theme/colors';
import { styled } from 'goober';
import { Z_INDEX_PROJECTS_TABLE_HEAD } from '@/theme/z-index';
import { ScrollableTableContainer } from '../shared/scrollable-table-container';
import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { useTranslation } from 'react-i18next';

export type ProjectSearchSuggestion = {
  basicData: SearchSuggestion;
  enrichedData: EnrichedProjectSearchSuggestion;
};

export const ProjectSuggestionsTable = ({
  data,
  hasNextPage,
  onShowMore,
}: {
  data: SearchSuggestion[];
  hasNextPage?: boolean;
  onShowMore?: () => void;
}) => {
  const { t } = useTranslation('default', { keyPrefix: 'globalSearch' });
  const searchSuggestionsProjectIds = data.map(suggestion => suggestion.id);
  const { data: enrichedProjectSuggestions, isLoading } =
    useEnrichedProjectSuggestions(searchSuggestionsProjectIds);

  const combinedProjectsData = useMemo(
    () => combineProjectData(data, enrichedProjectSuggestions),
    [data, enrichedProjectSuggestions],
  );

  const { rows, headers } = useProjectSuggestionsTableConfig(
    combinedProjectsData,
    isLoading,
  );

  if (!isLoading && !enrichedProjectSuggestions) {
    return null;
  }

  return (
    <ScrollableTableContainer>
      <Table data-testid="project-suggestions-results-table">
        <thead>
          <tr>
            {headers.cells.map(cell => (
              <TableHeaderCell key={cell.columnKey} cell={cell} />
            ))}
          </tr>
        </thead>
        <TableBody
          id="project-enriched-suggestions-table-body"
          rows={rows}
          isLoading={isLoading}
          borderColor={colors.gray.c3}
        />
        {!isLoading && hasNextPage ? (
          <tfoot>
            <tr>
              <td colSpan={headers.cells.length}>
                <Flexbox justify="center" margin="12px">
                  <Button variant="secondary" onClick={onShowMore}>
                    {t('showMore')}
                  </Button>
                </Flexbox>
              </td>
            </tr>
          </tfoot>
        ) : undefined}
      </Table>
    </ScrollableTableContainer>
  );
};

const Table = styled('table')`
  ${({ theme }) => theme.mixins.scrollbar};
  border-collapse: separate;
  overflow: auto;
  display: table;
  max-height: 100%;
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow.suggestionsTable};
  min-width: 100%;
  th {
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.colors.basics.white};
    z-index: ${Z_INDEX_PROJECTS_TABLE_HEAD};
  }
`;
